import { Link } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { customerName } from '../schemas/customer-rules';

import { CustomerStatusBadge } from './customer-status-badge';
import { DoNotSellBadge } from './do-not-sell-badge';

/**
 * Column model of the customers table; `meta` ties columns to the list's sorts and filters.
 * `currency` is the store's base currency, the one the backend sums `totalSpent` in.
 */
export function useCustomerColumns(storeId: string, currency: string) {
  const { t } = useTranslation('customers');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Customer>>>(
    () => [
      {
        id: 'name',
        header: t('columns.customer'),
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <Link
              to="/s/$storeId/customers/$customerId"
              params={{ storeId, customerId: row.original.id }}
              className="truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {customerName(row.original) ?? t('guestName')}
            </Link>
            <span className="text-muted-foreground truncate text-xs">{row.original.email}</span>
          </div>
        ),
        meta: { label: t('columns.customer'), sortField: 'name', lockVisibility: true },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            <CustomerStatusBadge status={row.original.status} />
            {row.original.doNotSell ? <DoNotSellBadge /> : null}
          </div>
        ),
        meta: { filterKey: 'status' },
        size: 150,
      },
      {
        id: 'ordersCount',
        header: t('columns.orders'),
        cell: ({ row }) => <span className="tabular-nums">{row.original.ordersCount}</span>,
        meta: { sortField: 'ordersCount', filterKey: 'ordersCount', align: 'end' },
        size: 90,
      },
      {
        id: 'totalSpent',
        header: t('columns.totalSpentIn', { currency }),
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">{format.money(row.original.totalSpent)}</span>
        ),
        meta: {
          label: t('columns.totalSpentIn', { currency }),
          sortField: 'totalSpent',
          filterKey: 'totalSpent',
          align: 'end',
        },
        size: 130,
      },
      {
        id: 'lastOrderAt',
        header: t('columns.lastOrder'),
        cell: ({ row }) =>
          row.original.lastOrderAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.lastOrderAt)}>
              {format.relative(row.original.lastOrderAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('noOrders')}</span>
          ),
        meta: { sortField: 'lastOrderAt', filterKey: 'lastOrderAt' },
        size: 140,
      },
      {
        id: 'acceptsMarketing',
        header: t('columns.marketing'),
        cell: ({ row }) =>
          row.original.acceptsMarketing ? (
            <span className="inline-flex items-center gap-1 text-sm">
              <Check className="text-success size-4" aria-hidden />
              {t('marketing.subscribed')}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">{t('marketing.notSubscribed')}</span>
          ),
        meta: { filterKey: 'acceptsMarketing' },
        size: 130,
      },
    ],
    [currency, format, storeId, t],
  );
}
